// src/context/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import {
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  getIdToken,
} from 'firebase/auth';
import { auth } from '@/config/firebase';
import { API_BASE_URL } from '@/config/api';

export type User = {
  customerId: string | null;
  guestSessionId: string | null;
  email: string | null;
  uid: string | null;
  isAuthenticated: boolean;
  role?: string;
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  gender?: string | null;
  dateOfBirth?: string | null;
};

type AuthContextType = {
  user: User;
  setUser: React.Dispatch<React.SetStateAction<User>>;
  logout: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  loading: boolean;
  welcomeShown: boolean;
  showSignInModal: boolean;
  setShowSignInModal: (show: boolean) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User>({
    customerId: null,
    guestSessionId: null,
    email: null,
    uid: null,
    isAuthenticated: false,
  });

  const [isAuthCheckComplete, setIsAuthCheckComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [welcomeShown, setWelcomeShown] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);

  // Initialize guest session
  const initializeGuest = () => {
    let guestId = localStorage.getItem('guestSessionId');
    if (!guestId) {
      guestId = `guest-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('guestSessionId', guestId);
    }

    setUser({
      customerId: null,
      guestSessionId: guestId,
      email: null,
      uid: null,
      isAuthenticated: false,
      role: 'guest',
    });
    setIsAuthCheckComplete(true);
  };

  // Merge guest cart after successful login
  const mergeGuestCart = async (idToken: string) => {
    const guestSessionId = localStorage.getItem('guestSessionId');
    if (!guestSessionId) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/cart/merge?guestSessionId=${guestSessionId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      if (res.ok) {
        localStorage.removeItem('guestSessionId');
        window.dispatchEvent(new Event('cart:refresh'));
        console.log('🛒 Guest cart merged successfully');
      }
    } catch (err) {
      console.warn('Cart merge failed (non-critical)', err);
    }
  };

  // Sync Firebase user with backend
  const syncWithBackend = async (firebaseUser: FirebaseUser) => {
    try {
      const idToken = await getIdToken(firebaseUser, true);

      // Verify token
      let res = await fetch(`${API_BASE_URL}/api/auth/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      });

      // If user doesn't exist, create one
      if (!res.ok && (res.status === 401 || res.status === 404)) {
        res = await fetch(`${API_BASE_URL}/api/auth/verify-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            email: firebaseUser.email,
            uid: firebaseUser.uid,
            createIfNotExists: true,
          }),
        });
      }

      if (!res.ok) {
        throw new Error('Backend authentication failed');
      }

      const backendUser = await res.json();

      // Fetch profile for extra details
      let profile = null;
      try {
        const profileRes = await fetch(`${API_BASE_URL}/api/users/me`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (profileRes.ok) profile = await profileRes.json();
      } catch (_) {}

      const updatedUser: User = {
        uid: firebaseUser.uid,
        customerId: backendUser.id.toString(),
        email: backendUser.email,
        role: backendUser.role || 'customer',
        isAuthenticated: true,
        guestSessionId: null,
        firstName: profile?.firstName || backendUser.firstName || null,
        lastName: profile?.lastName || backendUser.lastName || null,
        phoneNumber: profile?.phoneNumber || backendUser.phoneNumber || null,
        gender: profile?.gender || backendUser.gender || null,
        dateOfBirth: profile?.dateOfBirth || backendUser.dateOfBirth || null,
      };

      setUser(updatedUser);

      // Persist minimal data
      localStorage.setItem('userEmail', updatedUser.email ?? '');
      localStorage.setItem('customerId', updatedUser.customerId ?? '');
      localStorage.setItem('authMode', 'firebase');

      await mergeGuestCart(idToken);

      if (!welcomeShown) setWelcomeShown(true);

      console.log('✅ Auth successful:', updatedUser.email);
    } catch (error) {
      console.error('Auth sync failed:', error);
      // Fallback: still treat as authenticated
      setUser({
        uid: firebaseUser.uid,
        customerId: null,
        email: firebaseUser.email,
        isAuthenticated: true,
        guestSessionId: null,
        firstName: firebaseUser.email?.split('@')[0] || null,
      });
    } finally {
      setIsAuthCheckComplete(true);
    }
  };

  // Firebase auth listener (handles reload, token refresh, etc.)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        syncWithBackend(firebaseUser);
      } else {
        initializeGuest();
      }
    });

    return () => unsubscribe();
  }, []);

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Logout error:', err);
    }

    localStorage.removeItem('userEmail');
    localStorage.removeItem('customerId');
    localStorage.removeItem('authMode');

    initializeGuest();
    setWelcomeShown(false);
  };

  // Google Sign-In (using popup — proven to work from your SellerLogin)
  const signInWithGoogle = async () => {
    try {
      setLoading(true);

      const provider = new GoogleAuthProvider();
      // Add additional scopes if needed
      provider.addScope('email');
      provider.addScope('profile');
      // Force account selection
      provider.setCustomParameters({ 
        prompt: 'select_account',
        hd: undefined // Remove domain hint to allow all domains
      });

      console.log('🚀 Initiating Google sign-in...');
      const result = await signInWithPopup(auth, provider);

      // Log success but syncWithBackend will be called automatically by onAuthStateChanged
      console.log('✅ Google sign-in successful:', result.user.email);
    } catch (error: any) {
      console.error('❌ Google sign-in error:', error);

      // Handle specific error codes
      if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup was blocked by browser. Please allow popups for this site and try again.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        throw new Error('Sign-in was cancelled. Please try again.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in popup was closed. Please try again.');
      } else if (error.code === 'auth/unauthorized-domain') {
        throw new Error('This domain is not authorized for Google sign-in. Please contact support.');
      } else if (error.code === 'auth/operation-not-allowed') {
        throw new Error('Google sign-in is not enabled. Please contact support.');
      } else if (error.code === 'auth/invalid-api-key') {
        throw new Error('Invalid API configuration. Please contact support.');
      } else {
        // Generic error with more details for debugging
        throw new Error(error.message || 'Google sign-in failed. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading screen
  if (!isAuthCheckComplete) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-background z-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-primary mb-4" />
        <p className="text-lg font-medium">Loading your session...</p>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        logout,
        signInWithGoogle,
        loading,
        welcomeShown,
        showSignInModal,
        setShowSignInModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};